import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import client from "@/api";
import { useNavigate } from "react-router-dom";
import { BookOpen, Languages, GraduationCap, ChevronRight, LogIn, Shield } from "lucide-react";

export default function Landing() {
  const { t, lang, user, accessStatus } = useApp();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);

  useEffect(() => {
    client.get(`/public/overview?lang=${lang}`).then(({ data }) => setOv(data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (user) navigate(accessStatus === "approved" ? "/dashboard" : "/status");
  }, [user, accessStatus, navigate]);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="App min-h-screen bg-[#0d0d0d]">
      <Header variant="public" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="w-full h-full opacity-60"
            style={{ backgroundImage: "url('/header-bg.webp')", backgroundSize: "170%", backgroundPosition: "0% 20%", backgroundRepeat: "no-repeat" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/60 via-[#0d0d0d]/85 to-[#0d0d0d]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 min-h-[88vh] flex flex-col justify-center pt-28 pb-28 sm:pt-32 sm:pb-36">
          <div className="max-w-3xl fade-up">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#D21F2A] mb-5">{t("brand")}</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#FFFFFF]" data-testid="hero-title">
              {t("hero_title")}
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#D21F2A]">{t("hero_sub")}</p>
            <p className="mt-6 text-base sm:text-lg text-[#C9CDD3] leading-relaxed">{t("hero_desc")}</p>
            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
              <button data-testid="login-email-btn" onClick={() => navigate("/login")}
                className="btn-gold focusable px-7 py-3.5 rounded-full text-base flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" /> {t("login_email_cta")}
              </button>
              <button data-testid="login-google-btn" onClick={loginGoogle}
                className="focusable px-7 py-3.5 rounded-full text-base border border-[#D21F2A]/40 text-[#FFFFFF] hover:border-[#D21F2A] transition-colors flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" /> {t("cta_login_google")}
              </button>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            <Stat icon={BookOpen} value={ov?.chapter_count ?? 31} label={t("stat_chapters")} />
            <Stat icon={Languages} value={ov?.language_count ?? 8} label={t("stat_languages")} />
            <Stat icon={GraduationCap} value={ov?.final_question_count ?? 60} label={t("stat_final")} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#FFFFFF] mb-8">{t("how_title")}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[["how_1", "how_1d"], ["how_2", "how_2d"], ["how_3", "how_3d"]].map(([t1, d], i) => (
            <div key={i} className="card-dark rounded-2xl p-6" data-testid={`how-step-${i + 1}`}>
              <div className="w-9 h-9 rounded-full gold-bg text-white font-bold flex items-center justify-center mb-4">{i + 1}</div>
              <h3 className="font-serif text-xl text-[#FFFFFF] mb-2">{t(t1)}</h3>
              <p className="text-sm text-[#C9CDD3] leading-relaxed">{t(d)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Syllabus */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-24">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#FFFFFF] mb-8">{t("syllabus")}</h2>
        <div className="space-y-8">
          {ov?.areas?.map((a) => (
            <div key={a.area}>
              <div className="text-xs uppercase tracking-widest text-[#D21F2A] mb-3">{a.area_title}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {a.chapters.map((c) => (
                  <div key={c.chapter_id} data-testid={`syllabus-${c.chapter_id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10">
                    <span className="text-[#D21F2A] font-serif text-sm w-7">{String(c.order).padStart(2, "0")}</span>
                    <span className="text-[#FFFFFF] text-sm flex-1">{c.title}</span>
                    <span className="text-[10px] text-[#9AA0A6]">S. {c.pdf_pages}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button data-testid="cta-start-btn" onClick={() => navigate("/login")}
            className="btn-gold focusable px-8 py-4 rounded-full text-base inline-flex items-center gap-2">
            {t("cta_start")} <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#D21F2A]/10 py-8 text-center text-xs text-[#9AA0A6]">
        <p>{t("brand")}</p>
        <p className="mt-2 flex items-center justify-center gap-4">
          <a href="/impressum" data-testid="footer-impressum" className="hover:text-[#D21F2A] underline">{t("impressum")}</a>
          <a href="/datenschutz" data-testid="footer-datenschutz" className="hover:text-[#D21F2A] underline">{t("datenschutz")}</a>
          <button data-testid="footer-admin-btn" onClick={() => navigate("/admin-login")}
            className="focusable inline-flex items-center gap-1 hover:text-[#D21F2A] underline">
            <Shield className="w-3.5 h-3.5" /> {t("admin_area")}
          </button>
        </p>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="card-dark rounded-2xl p-5 flex items-center gap-4">
      <Icon className="w-8 h-8 text-[#D21F2A]" />
      <div>
        <div className="font-serif text-3xl text-[#FFFFFF]">{value}</div>
        <div className="text-xs text-[#C9CDD3]">{label}</div>
      </div>
    </div>
  );
}
